import * as React from 'react';

import { Box } from './box';
import type { BoxProps } from './box';

// Size mapping for icons (in pixels for accurate sizing)
const sizeMap = {
  xs: '12px',
  sm: '16px',
  md: '20px',
  lg: '24px',
  xl: '32px',
  '2xl': '40px',
  '3xl': '48px',
};

// Color mapping for icons (using actual color values)
// Using hex values directly since Chakra UI v3 token processing has issues
const colorMap = {
  current: 'currentColor',
  primary: '#0066CC', // TMS brand primary blue (brand.500)
  secondary: '#6C757D', // fg.muted - gray color
  tertiary: '#ADB5BD', // fg.subtle - lighter gray
  success: '#28A745', // success.500
  warning: '#FFC107', // warning.500
  danger: '#DC3545', // danger.500
  info: '#17A2B8', // info.500
  white: 'white',
  muted: '#ADB5BD', // fg.subtle - lighter gray for muted state
};

export interface IconProps extends Omit<BoxProps, 'boxSize'> {
  /** Icon size */
  size?: keyof typeof sizeMap;
  /** Icon color */
  iconColor?: keyof typeof colorMap;
  /** Makes the icon clickable */
  onClick?: () => void;
  /** Adds hover effect when clickable */
  hoverable?: boolean;
  /** Icon component from react-icons */
  children: React.ReactNode;
  /** Render as child element */
  asChild?: boolean;
  /** Legacy boxSize prop (maps to width/height) */
  boxSize?: string | number;
}

const Icon = React.forwardRef<HTMLDivElement, IconProps>(
  (
    {
      size = 'md',
      iconColor = 'current',
      onClick,
      hoverable,
      children,
      asChild,
      boxSize,
      ...props
    },
    ref
  ) => {
    const isClickable = !!onClick || hoverable;

    // Handle legacy boxSize prop
    const iconSize = boxSize || sizeMap[size as keyof typeof sizeMap];
    const iconWidth = typeof iconSize === 'string' ? iconSize : `${iconSize}`;
    const iconHeight = typeof iconSize === 'string' ? iconSize : `${iconSize}`;

    // If asChild is true, render children directly with props
    if (asChild && React.isValidElement(children)) {
      // Filter out Chakra UI style props that shouldn't go to DOM elements
      const {
        marginInlineEnd,
        // marginInlineStart: _marginInlineStart,
        // marginBlockEnd: _marginBlockEnd,
        // marginBlockStart: _marginBlockStart,
        // paddingInlineEnd: _paddingInlineEnd,
        // paddingInlineStart: _paddingInlineStart,
        // paddingBlockEnd,
        // paddingBlockStart,
        // inlineSize,
        // blockSize,
        // minInlineSize,
        // minBlockSize,
        // maxInlineSize,
        // maxBlockSize,
        ...restProps
      } = props;

      return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        ref,
        style: {
          width: iconWidth,
          height: iconHeight,
          color: restProps.color || colorMap[iconColor as keyof typeof colorMap],
          cursor: isClickable ? 'pointer' : undefined,
          transition: 'opacity 0.2s',
          marginInlineEnd: marginInlineEnd,
          ...(((children as React.ReactElement<Record<string, unknown>>).props?.style ||
            {}) as Record<string, unknown>),
        },
        onClick:
          onClick || (children as React.ReactElement<Record<string, unknown>>).props?.onClick,
        onKeyDown: isClickable
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : (children as React.ReactElement<Record<string, unknown>>).props?.onKeyDown,
        role: isClickable
          ? 'button'
          : (children as React.ReactElement<Record<string, unknown>>).props?.role,
        tabIndex: isClickable
          ? 0
          : (children as React.ReactElement<Record<string, unknown>>).props?.tabIndex,
        ...restProps,
      });
    }

    // Get the icon color value
    const iconColorValue = colorMap[iconColor as keyof typeof colorMap];

    // If children is a React element, apply size override
    // Color will be inherited from parent Box (currentColor)
    // This ensures SVG icons (Radix UI) properly inherit the color and size
    const sizedChildren = React.isValidElement(children)
      ? React.cloneElement(children, {
          // Override Radix UI's hardcoded width/height attributes
          width: undefined,
          height: undefined,
          style: {
            ...(((children.props as Record<string, unknown>)?.style as Record<string, unknown>) ||
              {}),
            // Force the size through CSS style
            // Color inherits from parent via currentColor
            width: iconWidth,
            height: iconHeight,
          },
        } as Record<string, unknown>)
      : children;

    return (
      <Box
        ref={ref}
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        width={iconWidth}
        height={iconHeight}
        color={iconColorValue}
        cursor={isClickable ? 'pointer' : undefined}
        onClick={onClick}
        _hover={isClickable ? { opacity: 0.8 } : undefined}
        transition="opacity 0.2s"
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={
          isClickable
            ? (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
        {...props}
      >
        {sizedChildren}
      </Box>
    );
  }
);

Icon.displayName = 'Icon';

export { Icon };

// Note: For icons, use react-icons library directly
// Example usage:
// import { LuChevronDown, LuTruck, LuMapPin } from 'react-icons/lu';
// <Icon size="md" iconColor="primary"><LuChevronDown /></Icon>
